import React from 'react';
import {
  Box,
  Text,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { FiMaximize2 } from 'react-icons/fi';
import { ChartData } from '../../types/chat';
import { toCurrency } from '../../utils/FormatFunctions';

interface ChartRendererProps {
  chart: ChartData;
}

const DEFAULT_COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
];

// Formata valores grandes do eixo Y para evitar que cortem
const formatYAxisTick = (value: number) => {
  if (value >= 1e12) {
    return `${(value / 1e12).toFixed(0)} T`;
  }
  if (value >= 1e9) {
    return `${(value / 1e9).toFixed(0)} B`;
  }
  if (value >= 1e6) {
    return `${(value / 1e6).toFixed(0)} M`;
  }
  if (value >= 1e3) {
    return `${(value / 1e3).toFixed(0)} K`;
  }
  return value.toString();
};

// Tooltip customizado com formatação monetária inteligente
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box bg="white" p={3} borderRadius="md" boxShadow="lg" border="1px" borderColor="gray.200">
        {Object.entries(data).map(([key, value]) => {
          // Detecta se é um valor monetário (verifica nomes de campos comuns)
          const isCurrency =
            typeof value === 'number' &&
            (key.toLowerCase().includes('faturamento') ||
              key.toLowerCase().includes('receita') ||
              key.toLowerCase().includes('valor') ||
              key.toLowerCase().includes('total') ||
              key.toLowerCase().includes('imposto') ||
              key.toLowerCase().includes('salario') ||
              key.toLowerCase().includes('custo') ||
              key.toLowerCase().includes('lucro') ||
              key.toLowerCase().includes('prejuizo'));

          return (
            <Text key={key} fontSize="sm" mb={1}>
              <strong>{key}:</strong> {isCurrency ? toCurrency(value as number) : String(value)}
            </Text>
          );
        })}
      </Box>
    );
  }
  return null;
};

export const ChartRenderer: React.FC<ChartRendererProps> = ({ chart }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cores = chart.cores || DEFAULT_COLORS;

  const renderLineChart = (height = 300) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chart.dados}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={chart.eixo_x || 'x'} />
        <YAxis tickFormatter={formatYAxisTick} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {chart.eixo_y?.map((key, index) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={cores[index % cores.length]}
            strokeWidth={2}
            name={chart.labels?.[key] || key}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderBarChart = (height = 300) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chart.dados}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={chart.eixo_x || 'x'} />
        <YAxis tickFormatter={formatYAxisTick} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {chart.eixo_y?.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            fill={cores[index % cores.length]}
            name={chart.labels?.[key] || key}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  const renderPieChart = (height = 300) => {
    // Para gráfico de pizza, assumimos que o primeiro eixo Y é o valor
    const dataKey = chart.eixo_y?.[0] || 'value';
    const nameKey = chart.eixo_x || 'name';

    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chart.dados}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            outerRadius={100}
            label
          >
            {chart.dados.map((item, index) => {
              const uniqueKey = `${nameKey ? item[nameKey] : index}-${dataKey ? item[dataKey] : index}`;
              return <Cell key={uniqueKey} fill={cores[index % cores.length]} />;
            })}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const renderAreaChart = (height = 300) => (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chart.dados}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={chart.eixo_x || 'x'} />
        <YAxis tickFormatter={formatYAxisTick} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {chart.eixo_y?.map((key, index) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            fill={cores[index % cores.length]}
            stroke={cores[index % cores.length]}
            name={chart.labels?.[key] || key}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );

  const renderScatterChart = (height = 300) => {
    const xKey = chart.eixo_x || 'x';
    const yKey = chart.eixo_y?.[0] || 'y';

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} name={chart.labels?.[xKey] || xKey} />
          <YAxis
            dataKey={yKey}
            name={chart.labels?.[yKey] || yKey}
            tickFormatter={formatYAxisTick}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
          <Legend />
          <Scatter name={chart.titulo} data={chart.dados} fill={cores[0]} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  const renderChart = (height = 300) => {
    switch (chart.tipo) {
      case 'line':
        return renderLineChart(height);
      case 'bar':
        return renderBarChart(height);
      case 'pie':
        return renderPieChart(height);
      case 'area':
        return renderAreaChart(height);
      case 'scatter':
        return renderScatterChart(height);
      case 'heatmap':
        // Heatmap é mais complexo, por enquanto renderiza como tabela
        return (
          <Text fontSize="sm" color="gray.500">
            Visualização de heatmap em desenvolvimento
          </Text>
        );
      default:
        return <Text>Tipo de gráfico não suportado: {chart.tipo}</Text>;
    }
  };

  return (
    <>
      <Box
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        p={4}
        mb={3}
        position="relative"
      >
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontWeight="bold" fontSize="md">
            {chart.titulo}
          </Text>
          <IconButton
            aria-label="Expandir gráfico"
            icon={<FiMaximize2 />}
            size="sm"
            variant="ghost"
            onClick={onOpen}
          />
        </Flex>
        {renderChart()}
      </Box>

      {/* Modal para visualização expandida */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{chart.titulo}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {chart.dados && chart.dados.length > 0 ? (
              renderChart(600)
            ) : (
              <Box className="flex h-[600px] items-center justify-center">
                <Text color="gray.500">Sem dados disponíveis</Text>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
